import { useTranslation } from 'react-i18next';

import { DEFAULT_QUANTITY_UNITS } from '../../components/quantityUnit';
import { ResourceConfigModal, readVendorDeviceMeta } from '../../components/resourceConfig';
import { SystemDiskSizeFormFields } from '../../components/systemDiskSizeField';
import { hasVendorDeviceSelection } from '../../components/vendorAndDeviceRadioList';
import { hasMeaningfulValue } from '../../utils';
import { isVendorQuantityOptional } from '../../utils/postPaidPricing';
import { SYSTEM_DISK_MODAL_PRICING_FIELD, SYSTEM_DISK_MODAL_SIZE_FIELD, SYSTEM_DISK_RESOURCE_MODAL_TITLE } from './constants';

export interface SystemDiskConfigModalProps {
  /** 计价模型 id；有值时弹窗内展示厂商设备 */
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  /** 原子资源 code，如 SystemVolume */
  resourceCode?: string;
  initialValues?: Record<string, unknown>;
  minSize: number;
  maxSize: number;
  markCount: number;
  translateUnit?: boolean;
  onOk: (values: Record<string, unknown>) => void;
  onCancel: () => void;
}

/** 系统盘大小是否已配置 */
function isSystemDiskSizeConfigured(values: Record<string, unknown>, sizeField = SYSTEM_DISK_MODAL_SIZE_FIELD): boolean {
  return hasMeaningfulValue(values[sizeField] ?? values.systemDiskSize);
}

/** 系统盘弹窗可否提交：有大小；计价时还需厂商设备；后付费/自定义规格创建仅需厂商设备 */
function canSubmitSystemDiskConfig(
  values: Record<string, unknown>,
  billModelId?: string,
  paymentTiming?: string,
  skuType?: string,
  resourceEditMode?: string,
  pricingField = SYSTEM_DISK_MODAL_PRICING_FIELD,
): boolean {
  const hasVendor = hasVendorDeviceSelection(readVendorDeviceMeta(values, pricingField));
  if (isVendorQuantityOptional({ billModelId, paymentTiming, skuType, resourceEditMode }) && hasVendor) {
    return true;
  }
  if (!isSystemDiskSizeConfigured(values)) return false;
  if (!billModelId) return true;
  return hasVendor;
}

/** 系统盘配置弹窗：容量滑条 +（可选）计价厂商设备，对齐 compute Memory 弹窗 */
export function SystemDiskConfigModal({
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  resourceCode,
  initialValues,
  minSize,
  maxSize,
  markCount,
  translateUnit = false,
  onOk,
  onCancel,
}: SystemDiskConfigModalProps) {
  const { t } = useTranslation();
  const skipSizeRequired = isVendorQuantityOptional({ billModelId, paymentTiming, skuType, resourceEditMode });
  const vendorDeviceDisabled =
    resourceEditMode === 'order-custom' &&
    hasVendorDeviceSelection(readVendorDeviceMeta((initialValues ?? {}) as Record<string, unknown>, SYSTEM_DISK_MODAL_PRICING_FIELD));

  return (
    <ResourceConfigModal
      visible
      title={SYSTEM_DISK_RESOURCE_MODAL_TITLE}
      resourceCode={resourceCode ?? 'SystemDisk'}
      vendorDevicePrefix={SYSTEM_DISK_MODAL_PRICING_FIELD}
      initialValues={initialValues}
      billModelId={billModelId}
      deploymentTypeId={deploymentTypeId}
      paymentTiming={paymentTiming}
      skuType={skuType}
      resourceEditMode={resourceEditMode}
      vendorDeviceHidden={!billModelId}
      vendorDeviceDisabled={vendorDeviceDisabled}
      validateFields={skipSizeRequired ? [] : [SYSTEM_DISK_MODAL_SIZE_FIELD]}
      canSubmit={(values) => canSubmitSystemDiskConfig(values, billModelId, paymentTiming, skuType, resourceEditMode)}
      quantityDefaultUnit={DEFAULT_QUANTITY_UNITS.disk}
      lockQuantityDisplayToDefault
      onOk={onOk}
      onCancel={onCancel}
    >
      {(_form, { quantityUnit }) => (
        <SystemDiskSizeFormFields
          sizeField={SYSTEM_DISK_MODAL_SIZE_FIELD}
          minSize={minSize}
          maxSize={maxSize}
          markCount={markCount}
          translateUnit={translateUnit}
          quantityUnit={quantityUnit}
          billModelId={billModelId}
          paymentTiming={paymentTiming}
          skuType={skuType}
          resourceEditMode={resourceEditMode}
          requiredMessage={t('data-center:please.enter.system.disk.size')}
        />
      )}
    </ResourceConfigModal>
  );
}
